// Persistence for first-time-only hint toast flags (daily mode).
//
// Three flags today (blueShown, orangeShown, incorrectShown), one localStorage
// key. Used by the daily-mode hint evaluator in main.ts to skip toasts after
// their first firing. Practice-mode behavior is handled separately (per-puzzle,
// not persisted).
//
// Schema versioned via `v` so a future schema change can migrate or reset
// cleanly. v2 added `incorrectShown` — v1 blobs migrate forward, preserving
// `blueShown` / `orangeShown` so existing players don't see those hints again.
// wipeAllPersistence() in persistence.ts clears this key too.

export const HINT_FLAGS_KEY = 'letterflick:hintFlags' as const

export interface HintFlags {
  readonly v: 2
  readonly blueShown: boolean
  readonly orangeShown: boolean
  readonly incorrectShown: boolean
}

const DEFAULT_FLAGS: HintFlags = {
  v: 2,
  blueShown: false,
  orangeShown: false,
  incorrectShown: false,
}

export function loadHintFlags(): HintFlags {
  let raw: string | null
  try {
    raw = localStorage.getItem(HINT_FLAGS_KEY)
  } catch {
    return DEFAULT_FLAGS
  }
  if (raw === null) return DEFAULT_FLAGS
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || !('v' in parsed)) {
      return DEFAULT_FLAGS
    }
    const obj = parsed as Record<string, unknown>
    const blueShown = obj['blueShown'] === true
    const orangeShown = obj['orangeShown'] === true
    if (obj['v'] === 2) {
      const incorrectShown = obj['incorrectShown'] === true
      return { v: 2, blueShown, orangeShown, incorrectShown }
    }
    if (obj['v'] === 1) {
      // v1 → v2: seed incorrectShown=false so existing players see the new
      // "no-highlight = wrong" hint the first time they miss.
      return { v: 2, blueShown, orangeShown, incorrectShown: false }
    }
  } catch {
    // Fall through to default.
  }
  return DEFAULT_FLAGS
}

export function saveHintFlags(flags: HintFlags): void {
  try {
    localStorage.setItem(HINT_FLAGS_KEY, JSON.stringify(flags))
  } catch {
    // Swallow — saving is best-effort.
  }
}
